/**
 * One look for every chart.
 *
 * Colours are CSS variables (defined per theme in styles.css), not hex. Four
 * chart screens each carried their own hard-coded palette, which meant a grid
 * line chosen for white was invisible on dark, a tooltip stayed a white box in
 * dark mode, and the "calls" series was a different blue on every screen.
 * Recharts passes these strings straight through to SVG attributes and inline
 * styles, where `var()` resolves like anywhere else -- so a theme switch
 * recolours a chart with no re-render.
 *
 * Series 1 is the brand ramp, so it follows the operator's accent.
 */

const v = (name: string) => `rgb(var(--${name}))`;

/** Categorical palette, in order. Index modulo length for long series. */
export const CHART_COLORS = [
  v('chart-1'),
  v('chart-2'),
  v('chart-3'),
  v('chart-4'),
  v('chart-5'),
  v('chart-6'),
  v('chart-7'),
  v('chart-8'),
] as const;

/**
 * Named series colours, for the charts whose series have a fixed meaning.
 * A "qualified" line is green on every screen, a "cost" line red.
 */
export const SERIES = {
  primary: v('chart-1'),
  success: v('chart-2'),
  warning: v('chart-3'),
  violet: v('chart-4'),
  danger: v('chart-5'),
  info: v('chart-6'),
  neutral: v('chart-8'),
} as const;

export const AXIS_COLOR = v('chart-axis');
export const GRID_COLOR = v('chart-grid');

/** Spread onto <XAxis> / <YAxis>. Quiet: no axis line, no tick marks. */
export const axisProps = {
  stroke: GRID_COLOR,
  tick: { fontSize: 11, fill: AXIS_COLOR },
  tickLine: false,
  axisLine: false,
} as const;

/** Spread onto <CartesianGrid>. Horizontal hairlines only. */
export const gridProps = {
  stroke: GRID_COLOR,
  strokeDasharray: '3 3',
  vertical: false,
} as const;

/** The same, for horizontal bar charts, where the value axis is X. */
export const gridPropsVertical = {
  stroke: GRID_COLOR,
  strokeDasharray: '3 3',
  horizontal: false,
} as const;

/** Spread onto <Tooltip>. A surface card in both themes, not a white box. */
export const tooltipProps = {
  contentStyle: {
    fontSize: 12,
    borderRadius: 8,
    border: '1px solid rgb(var(--line))',
    background: 'rgb(var(--raised))',
    color: 'rgb(var(--fg))',
    boxShadow: '0 8px 24px -8px rgb(16 24 40 / 0.18)',
    padding: '8px 10px',
  },
  labelStyle: { color: 'rgb(var(--fg))', fontWeight: 600, marginBottom: 4 },
  itemStyle: { color: 'rgb(var(--fg))', padding: '1px 0' },
  // The hover cursor: a band on bar charts, a line on line/area charts.
  cursor: { fill: 'rgb(var(--sunken))', stroke: 'rgb(var(--line-strong))' },
} as const;

/** Spread onto <Legend>. */
export const legendProps = {
  iconType: 'circle' as const,
  iconSize: 8,
  wrapperStyle: { fontSize: 12, color: 'rgb(var(--fg-muted))' },
};
